import { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import {
  updateOrganization,
  deleteOrganization,
  deleteOrganizationLogo,
  getOrgLogoUploadUrl,
  confirmOrgLogoUpload,
  listPaymentClients,
  upsertTest,
  type ErrorResponse,
  type UserOrganization,
  type PaymentClientSummary,
} from '@vn-propane/shared';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SUPPORTED_CURRENCIES, SUPPORTED_COUNTRIES, getCountryFlag, getImageUrl } from '@/lib/utils';
import { Loader2, Upload, X, ImageIcon } from 'lucide-react';
import { MomoPayCard } from '@/components/payment/MomoPayCard';

type OutletContext = {
  organization: UserOrganization;
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const { organization } = useOutletContext<OutletContext>();

  // Organization settings state
  const [name, setName] = useState(organization.name);
  const [description, setDescription] = useState(organization.description || '');
  const [currency, setCurrency] = useState(organization.currency);
  const [defaultCountry, setDefaultCountry] = useState(organization.default_country);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Payment settings state
  const [paymentClients, setPaymentClients] = useState<PaymentClientSummary[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [isTogglingTest, setIsTogglingTest] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<string | null>(null);

  // Logo state
  const [logoUrl, setLogoUrl] = useState<string | null>(
    getImageUrl(organization.logo_file_id)
  );
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = organization.role === 'admin';

  // Sync logo with organization
  useEffect(() => {
    setLogoUrl(getImageUrl(organization.logo_file_id));
  }, [organization.logo_file_id]);

  // Load payment clients
  useEffect(() => {
    async function loadPaymentClients() {
      if (!id || !isAdmin) {
        setIsLoadingPayments(false);
        return;
      }

      try {
        const result = await listPaymentClients({ path: { org_id: id } });
        if (result.data?.data) {
          setPaymentClients(result.data.data);
        }
      } catch {
        // Silently fail - payment settings are optional
      } finally {
        setIsLoadingPayments(false);
      }
    }

    loadPaymentClients();
  }, [id, isAdmin]);

  const isTestPaymentEnabled = paymentClients.some(
    (c) => c.provider === 'test' && c.isEnabled
  );

  const handleToggleTestPayment = async (enabled: boolean) => {
    if (!id) return;

    setIsTogglingTest(true);
    setPaymentError(null);

    try {
      const result = await upsertTest({
        path: { org_id: id },
        body: { isEnabled: enabled },
      });

      if (result.data?.data) {
        // Update local state
        setPaymentClients((prev) => {
          const existing = prev.find((c) => c.provider === 'test');
          if (existing) {
            return prev.map((c) =>
              c.provider === 'test' ? { ...c, isEnabled: enabled } : c
            );
          } else {
            // Re-fetch to get the full object from server
            listPaymentClients({ path: { org_id: id } }).then((res) => {
              if (res.data?.data) {
                setPaymentClients(res.data.data);
              }
            });
            return prev;
          }
        });
      } else if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setPaymentError(errorResponse.error?.message || t('payment.failedToUpdate'));
      }
    } catch {
      setPaymentError(t('payment.failedToUpdate'));
    } finally {
      setIsTogglingTest(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setError(null);
    setSuccess(null);
    setIsSaving(true);

    try {
      const result = await updateOrganization({
        path: { id },
        body: {
          name,
          description: description || undefined,
          currency,
          defaultCountry,
        },
      });

      if (result.data) {
        setSuccess(t('organizationSettings.updateSuccess'));
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('organizationSettings.failedToUpdate'));
      } else {
        setError(t('organizationSettings.failedToUpdate'));
      }
    } catch {
      setError(t('organizationSettings.unexpectedError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setError(null);
    setIsDeleting(true);

    try {
      const result = await deleteOrganization({
        path: { id },
      });

      if (result.data) {
        navigate('/organizations');
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('organizationSettings.failedToDelete'));
      } else {
        setError(t('organizationSettings.failedToDelete'));
      }
    } catch {
      setError(t('organizationSettings.unexpectedError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoError(t('organizationSettings.logoMustBeImage'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t('organizationSettings.logoTooLarge'));
      return;
    }

    setUploadingLogo(true);
    setLogoError(null);

    try {
      // Step 1: Get presigned URL from backend
      const uploadUrlResult = await getOrgLogoUploadUrl({
        path: { id },
        body: {
          fileType: 'organization_logo',
          referenceId: id,
          contentType: file.type,
          filename: file.name,
          fileSizeBytes: file.size,
        },
      });

      if (uploadUrlResult.error) {
        const errorResponse = uploadUrlResult.error as ErrorResponse;
        throw new Error(errorResponse.error?.message || t('organizationSettings.logoUploadFailed'));
      }

      const { uploadId, uploadUrl } = uploadUrlResult.data!.data;

      // Step 2: Upload file directly to S3 using presigned URL
      // Must include x-amz-acl header since it's part of the presigned signature
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
          'x-amz-acl': 'public-read',
        },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error(t('organizationSettings.logoUploadFailed'));
      }

      // Step 3: Confirm the upload with backend
      const confirmResult = await confirmOrgLogoUpload({
        path: { id, upload_id: uploadId },
        body: {
          fileSizeBytes: file.size,
        },
      });

      if (confirmResult.error) {
        const errorResponse = confirmResult.error as ErrorResponse;
        throw new Error(errorResponse.error?.message || t('organizationSettings.logoUploadFailed'));
      }

      // Use getImageUrl to build full API URL from the upload ID
      setLogoUrl(getImageUrl(confirmResult.data?.data.upload.id));
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : t('organizationSettings.logoUploadFailed'));
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = async () => {
    if (!id) return;

    setUploadingLogo(true);
    setLogoError(null);

    try {
      const result = await deleteOrganizationLogo({
        path: { id },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        throw new Error(errorResponse.error?.message || t('organizationSettings.logoDeleteFailed'));
      }

      setLogoUrl(null);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : t('organizationSettings.logoDeleteFailed'));
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold tracking-tight">{t('organizationSettings.title')}</h2>
        <p className="text-muted-foreground">{t('organizationSettings.subtitle')}</p>
      </div>

      {/* Organization Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('organizationSettings.organizationDetails')}</CardTitle>
          <CardDescription>
            {isAdmin
              ? t('organizationSettings.updateDescription')
              : t('organizationSettings.viewOnlyDescription', { role: organization.role })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('organizations.organizationName')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isSaving || !isAdmin}
                placeholder={t('organizations.enterOrganizationName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('organizations.description')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving || !isAdmin}
                placeholder={t('organizations.enterDescription')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">{t('organizations.currency')}</Label>
              <Select
                value={currency}
                onValueChange={setCurrency}
                disabled={isSaving || !isAdmin}
              >
                <SelectTrigger id="currency">
                  <SelectValue placeholder={t('organizations.selectCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code} - {t(`currencies.${code}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="defaultCountry">{t('organizations.defaultCountry')}</Label>
              <Select
                value={defaultCountry}
                onValueChange={setDefaultCountry}
                disabled={isSaving || !isAdmin}
              >
                <SelectTrigger id="defaultCountry">
                  <SelectValue placeholder={t('organizations.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_COUNTRIES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {getCountryFlag(code)} {t(`countries.${code}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t('organizations.defaultCountryDescription')}
              </p>
            </div>
            {isAdmin && (
              <Button type="submit" disabled={isSaving}>
                {isSaving ? t('common:buttons.saving') : t('common:buttons.save')}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Logo Settings */}
      {isAdmin && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('organizationSettings.logo')}</CardTitle>
            <CardDescription>
              {t('organizationSettings.logoDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              {/* Logo preview */}
              <div className="relative w-24 h-24 border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                {logoUrl ? (
                  <>
                    <img
                      src={logoUrl}
                      alt={t('organizationSettings.logoAlt')}
                      className="w-full h-full object-contain"
                    />
                    {!uploadingLogo && (
                      <button
                        onClick={handleLogoDelete}
                        className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                        title={t('organizationSettings.removeLogo')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </>
                ) : (
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                )}
                {uploadingLogo && (
                  <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                )}
              </div>

              {/* Upload button */}
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="org-logo-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {logoUrl ? t('organizationSettings.changeLogo') : t('organizationSettings.uploadLogo')}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t('organizationSettings.logoHint')}
                </p>
                {logoError && (
                  <p className="text-xs text-destructive">{logoError}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Settings */}
      {isAdmin && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('payment.processors')}</CardTitle>
            <CardDescription>
              {t('payment.processorsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {paymentError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{paymentError}</AlertDescription>
              </Alert>
            )}
            {paymentSuccess && (
              <Alert className="mb-4">
                <AlertDescription>{paymentSuccess}</AlertDescription>
              </Alert>
            )}

            {isLoadingPayments ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('common:loading')}</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Test Payment Provider */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="test-payment" className="text-base font-medium">
                      {t('payment.testProvider')}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('payment.testProviderDescription')}
                    </p>
                  </div>
                  <Switch
                    id="test-payment"
                    checked={isTestPaymentEnabled}
                    onCheckedChange={handleToggleTestPayment}
                    disabled={isTogglingTest}
                  />
                </div>

                {/* MomoPay */}
                <MomoPayCard
                  orgId={id!}
                  paymentClient={paymentClients.find((c) => c.provider === 'momopay')}
                  onUpdate={setPaymentClients}
                  onSuccess={(msg) => {
                    setPaymentSuccess(msg);
                    setPaymentError(null);
                  }}
                  onError={(msg) => {
                    setPaymentError(msg);
                    setPaymentSuccess(null);
                  }}
                />

                {/* VNPay - Coming Soon */}
                <div className="flex items-center justify-between p-4 border rounded-lg border-dashed opacity-60">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">
                      VNPay
                      <span className="ml-2 text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {t('common:labels.comingSoon')}
                      </span>
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t('payment.vnpayDescription')}
                    </p>
                  </div>
                  <Switch disabled checked={false} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Danger Zone */}
      {isAdmin && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">{t('organizationSettings.dangerZone')}</CardTitle>
            <CardDescription>
              {t('organizationSettings.deleteWarning')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showDeleteConfirm ? (
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? t('organizationSettings.deleting') : t('organizationSettings.confirmDelete')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  {t('common:buttons.cancel')}
                </Button>
              </div>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
              >
                {t('organizationSettings.deleteOrganization')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
